import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './style.scss';
import useBluetooth from '../../hooks/useBluetooth'
import { useCollapse, useScanner } from '../../hooks';
import { Switch, Tooltip, notification } from 'antd';
import { ExclamationCircleOutlined, InfoCircleFilled } from '@ant-design/icons';
import { sendTextInChunks } from '../../utils';
import notScan from '../../assets/licensePlate/not-scan.png'
import { checkCamera } from '../../utils/cameraCheck';
import notCamera from '../../assets/licensePlate/not-camera.png'

const QRCode = () => {
    const timer = useRef(null);
    const bluetooth = useSelector(state => state.speaker.bluetooth);
    const SWBOOL = true
    const [coordinates, setCoordinates] = useState(null)
    const [switchChecked, setSwitchChecked] = useState(SWBOOL) // 只控制Switch的状态
    const showBorderRef = useRef(SWBOOL)
    const showCoordinateRef = useRef(true) // 默认显示坐标系
    const { sendMessage, setupNotifications, stopNotifications } = useBluetooth();
    const imageRef = useRef(null)
    // 开启摄像头
    const canvasRef = useRef(null)
    const videoCoordinateRef = useRef(null)
    // 是否有图像
    const [hasImage, setHasImage] = useState(false)
    // 摄像头是否有用
    const [isCameraAvailable, setIsCameraAvailable] = useState(true)
    // 是否加载中
    const [loading, setLoading] = useState(false)
    const msgId = useRef(null);
    // 扫描
    const {videoRef, start, stop, sendFrame, imageDetail} = useScanner({
        autoScan: false,
        onResult: (res) => {
            if (!res.imageData || !res.result) return
            const angle = getQRCodeAngle(res.result.location)
            const coord = calculateQRCodeCoordinates(res.result.location, res.imageData.width, res.imageData.height)
            setCoordinates({
                angle,
                coord,
                text: res.result.data
            })
            imageRef.current = res
            setHasImage(true)
            // 移除立即绘制，改为在 useEffect 中处理
        }
    })
    // 绘制canvas
    const drawCanvas = useCallback((imageData, location = null) => {
        if (videoRef.current && canvasRef.current) {
            console.log(imageData)
            // 使用正方形尺寸，与拍照页保持一致
            const canvasSize = Math.min(imageData.width, imageData.height)
            canvasRef.current.width = canvasSize
            canvasRef.current.height = canvasSize
            const ctx = canvasRef.current.getContext('2d')
            
            // 绘制图像 - 居中显示
            const offsetX = (canvasSize - imageData.width) / 2
            const offsetY = (canvasSize - imageData.height) / 2
            ctx.putImageData(imageData, offsetX, offsetY)
            
            // 根据开关状态决定是否绘制坐标系
            if (showCoordinateRef.current) {
                drawCoordinateSystem(ctx, canvasSize, canvasSize)
            }
            
            // 根据开关状态决定是否绘制边框
            if (location && showBorderRef.current) {
                // 调整边框位置以适应居中显示
                const adjustedLocation = {
                    topLeftCorner: {
                        x: location.topLeftCorner.x + offsetX,
                        y: location.topLeftCorner.y + offsetY
                    },
                    topRightCorner: {
                        x: location.topRightCorner.x + offsetX,
                        y: location.topRightCorner.y + offsetY
                    },
                    bottomRightCorner: {
                        x: location.bottomRightCorner.x + offsetX,
                        y: location.bottomRightCorner.y + offsetY
                    },
                    bottomLeftCorner: {
                        x: location.bottomLeftCorner.x + offsetX,
                        y: location.bottomLeftCorner.y + offsetY
                    }
                }
                drawQRCodeBorder(ctx, adjustedLocation)
            }
        }
    }, [videoRef, canvasRef])

    // 边界开关 - 最小化重新渲染版本
    const handleChangeBorder = useCallback((checked) => {
        showBorderRef.current = checked // 更新ref
        setSwitchChecked(checked) // 只更新Switch状态
        
        // 立即重新绘制canvas
        if (imageRef.current?.imageData) {
            drawCanvas(imageRef.current.imageData, imageRef.current.result?.location)
        }
    }, [drawCanvas])

    // 绘制video上的坐标系
    const drawVideoCoordinateSystem = useCallback(() => {
        if (videoRef.current && videoCoordinateRef.current) {
            const video = videoRef.current
            const canvas = videoCoordinateRef.current
            
            // 设置canvas尺寸为正方形，使用较小的尺寸确保完全显示
            const videoSize = Math.min(video.videoWidth || video.clientWidth, video.videoHeight || video.clientHeight)
            canvas.width = videoSize
            canvas.height = videoSize
            
            const ctx = canvas.getContext('2d')
            drawCoordinateSystem(ctx, canvas.width, canvas.height)
        }
    }, [videoRef])


    // 监听video尺寸变化，重新绘制坐标系
    useEffect(() => {
        if (videoRef.current && videoCoordinateRef.current) {
            const video = videoRef.current
            
            const handleResize = () => {
                drawVideoCoordinateSystem()
            }
            
            // 监听video的loadedmetadata事件
            video.addEventListener('loadedmetadata', handleResize)
            
            // 初始绘制
            drawVideoCoordinateSystem()
            
            return () => {
                video.removeEventListener('loadedmetadata', handleResize)
            }
        }
    }, [drawVideoCoordinateSystem, videoRef])

    // 监听 hasImage 状态变化，确保在 DOM 更新后再绘制
    useEffect(() => {
        if (hasImage && imageRef.current?.imageData) {
            // 使用 setTimeout 确保 DOM 已经更新
            setTimeout(() => {
                drawCanvas(imageRef.current.imageData, imageRef.current.result?.location)
            })
        }
    }, [hasImage, drawCanvas, imageDetail])
  
    
    // 在组件挂载时启动扫描，卸载时停止扫描
    useEffect(() => {
        // 校验摄像头是否有用
        checkCamera().then(res => {
            if (res.available) {
                setIsCameraAvailable(true)
                start()
            } else {
                setIsCameraAvailable(false)
                notification.error({
                    message: '错误',
                    description: res.error,
                })
            }
        })
        // 组件卸载时清理资源
        return () => {
            stop()
        }
    }, [])
    
    const openerRef = useRef(null)
    const onMessageReceived = useCallback(async (result) => {
        const data = result.find((item) => item.includes('plugin'));
        if (!data) {
            return;
        }
        const slice = data.split('//');
        console.log('slice', slice)
        console.log(`命令：${slice[2]}, uid：${slice[3]}`)
        const uid = slice[3];
        if (slice.includes('is_connect_ailab')) {
            sendMessage({ [uid]: `${uid}_success` })
        }
        // 指令
        const command = slice[2];
        console.log(command, 'command')
        msgId.current = uid
        // 扫描
        if (command === 'video_scanning') {
            try {
                setLoading(true)
                const res = await sendFrame()
                if (!res) {
                    notification.error({
                        message: '二维码识别失败',
                        description: '没有检测到二维码，请调整图像。'
                    })
                    sendMessage({ [uid]: {
                        content: '扫描无结果',
                        rotation: 0,
                        index: 1,
                        total: 1
                    }})
                } else if (res && res.result && res.result.data) {
                    const rotation = getQRCodeAngle(res.result.location)
                    // 内容没超过
                    if (res.result.data.length <= 300) {
                        sendTextInChunks(res.result.data.slice(0, 300), (content, index, total) => {
                            sendMessage({ [uid]: {
                                content,
                                rotation: rotation.toFixed(2),
                                index,
                                total
                            } })
                        })
                    // 内容超过了
                    } else {
                        sendTextInChunks('数据量过大，请在 AI 实验平台查看', (content, index, total) => {
                            sendMessage({ [uid]: {
                                content,
                                rotation: rotation.toFixed(2),
                                index,
                                total
                            } })
                        })
                    }
                }
            } catch (error) {
                console.log(error)
                sendMessage({ [uid]: `${uid}_error`})
            } finally {
                setLoading(false)
            }
        // 打开草料二维码, 生成二维码
        } else if (command === 'open_scan_url') {
            try {
                // 检查是否已经有打开的窗口
                if (openerRef.current && !openerRef.current.closed) {
                    // 如果窗口已存在且未关闭，则聚焦到该窗口
                    console.log('二维码生成器页面已存在，聚焦到现有窗口')
                    openerRef.current.focus()
                    sendMessage({ [uid]: `${uid}_success` });
                } else {
                    // 如果没有打开的窗口，则创建新窗口
                    openerRef.current = window.open('https://cli.im/')
                     
                    // 检测窗口是否打开成功
                    if (openerRef.current && !openerRef.current.closed) {
                        // 保存窗口引用到全局变量
                        console.log('二维码生成器页面打开成功')
                        sendMessage({ [uid]: `${uid}_success` });
                            
                        // 监听窗口关闭事件，清理引用
                        openerRef.current.onbeforeunload = () => {
                            openerRef.current = null
                            console.log('二维码生成器页面已关闭')
                        }
                    } else {
                        console.log('二维码生成器页面打开失败')
                        sendMessage({ [uid]: `${uid}_error` })
                    }
                 }
            } catch (error) {
                console.error('打开页面时发生错误:', error)
                sendMessage({ [uid]: `${uid}_error` })
            }
        // 获取坐标
        } else if (command === 'get_coordinates') {
            const args = slice[4]
            if (args) {
                try {
                    const arr = args.split(' ')
                    let res
                    // 中心点
                    if (arr.length === 1) {
                        const _arr = arr[0].split(',')
                        if (_arr[0] === 'center') {
                            const label = _arr[1]
                            if (coordinates) {
                                res = coordinates.coord.center[label]
                            }
                        }
                    } else if (arr.length === 2) {
                        const reg = /(-[lr])/
                        const pos = arr[0] && arr[0].replace(reg, tar => tar[1].toUpperCase())
                        const label = arr[1] && arr[1].split(',')[1]
                        if (coordinates) {
                            const targetPos = coordinates.coord[pos]
                            // 原点
                            res = targetPos[label]
                        }
                    }
                    console.log(res, 'res!!!')
                    sendMessage({ [uid]: {coordinates:res} })
                    if (!res) {
                        sendMessage({ [uid]: `${uid}_error` })
                    }
                } catch (error) {
                    console.log(error, 'error!!!')
                    sendMessage({ [uid]: `${uid}_error` })
                }
            } else {
                sendMessage({ [uid]: `${uid}_error` })
            }
                 // 重置
         } else if (command === 'reset') {
             setCoordinates(null)
             
             // 清空画布
             if (canvasRef.current) {
                 const ctx = canvasRef.current.getContext('2d')
                 if (ctx) {
                     // 清空整个画布区域
                     ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
                     imageRef.current = null
                     setHasImage(false)
                     console.log('画布已清空')
                 }
             }
             
             sendMessage({ [uid]: `${uid}_success` })
         }
       
    }, [sendMessage, coordinates, sendFrame])

    // 在组件加载时检查蓝牙连接状态并设置监听
    useEffect(() => {
        setupNotifications(onMessageReceived)
        const currentTimer = timer.current;
        return () => {
            if (currentTimer) {
                clearInterval(currentTimer);
            }
            stopNotifications(onMessageReceived)
        };
    }, [bluetooth, setupNotifications, stopNotifications, onMessageReceived]);


  function getQRCodeAngle(location) {
    const { topLeftCorner, topRightCorner } = location
    const dx = topRightCorner.x - topLeftCorner.x
    const dy = topRightCorner.y - topLeftCorner.y
    let angle = Math.atan2(dy, dx) * (180 / Math.PI) // 原始角度（弧度→角度）

    // 转换到你的规定：
    // jsQR 默认 0°水平向右，顺时针为正
    // 我们希望 0° = 头向左 → +90° 偏移
    angle = angle + 90

    // 规范到 [-180, 180]
    if (angle > 180) angle -= 360
    if (angle < -180) angle += 360

    return angle
}


    // 坐标转换函数：将像素坐标转换为标准坐标系
    function convertToStandardCoords(pixelX, pixelY, canvasWidth, canvasHeight) {
        // 标准化分辨率 - 与坐标系绘制函数保持一致
        const STANDARD_WIDTH = 1920  // 对应 -960 到 960
        const STANDARD_HEIGHT = 1920 // 对应 -960 到 960
        
        // 将像素坐标转换为0-1范围
        const normalizedX = pixelX / canvasWidth
        const normalizedY = pixelY / canvasHeight
        
        // 转换为标准坐标系：中心为原点，横坐标[-960, 960]，纵坐标[-960, 960]
        const standardX = Math.round((normalizedX - 0.5) * STANDARD_WIDTH)
        const standardY = Math.round((0.5 - normalizedY) * STANDARD_HEIGHT) // 注意Y轴方向
        
        return { x: standardX, y: standardY }
    }
    
    // 计算二维码标准化坐标
    function calculateQRCodeCoordinates(location, canvasWidth, canvasHeight) {
        // 使用正方形尺寸，与drawCanvas函数保持一致
        const canvasSize = Math.min(canvasWidth, canvasHeight)
        
        // 计算图像在canvas中的偏移量
        const offsetX = (canvasSize - canvasWidth) / 2
        const offsetY = (canvasSize - canvasHeight) / 2
        
        // 调整二维码位置以适应居中显示
        const adjustedLocation = {
            topLeftCorner: {
                x: location.topLeftCorner.x + offsetX,
                y: location.topLeftCorner.y + offsetY
            },
            topRightCorner: {
                x: location.topRightCorner.x + offsetX,
                y: location.topRightCorner.y + offsetY
            },
            bottomRightCorner: {
                x: location.bottomRightCorner.x + offsetX,
                y: location.bottomRightCorner.y + offsetY
            },
            bottomLeftCorner: {
                x: location.bottomLeftCorner.x + offsetX,
                y: location.bottomLeftCorner.y + offsetY
            }
        }
        
        // 转换四个角的坐标，使用正方形canvas尺寸
        const topLeft = convertToStandardCoords(adjustedLocation.topLeftCorner.x, adjustedLocation.topLeftCorner.y, canvasSize, canvasSize)
        const topRight = convertToStandardCoords(adjustedLocation.topRightCorner.x, adjustedLocation.topRightCorner.y, canvasSize, canvasSize)
        const bottomRight = convertToStandardCoords(adjustedLocation.bottomRightCorner.x, adjustedLocation.bottomRightCorner.y, canvasSize, canvasSize)
        const bottomLeft = convertToStandardCoords(adjustedLocation.bottomLeftCorner.x, adjustedLocation.bottomLeftCorner.y, canvasSize, canvasSize)
        
        // 计算二维码中心点坐标
        const centerX = Math.round((topLeft.x + topRight.x + bottomRight.x + bottomLeft.x) / 4)
        const centerY = Math.round((topLeft.y + topRight.y + bottomRight.y + bottomLeft.y) / 4)
        
        const coordinates = {
            topLeft,
            topRight,
            bottomRight,
            bottomLeft,
            center: { x: centerX, y: centerY }
        }
        
        console.log('标准化坐标:', coordinates)
        return coordinates
    }

    // 绘制坐标系的函数
    function drawCoordinateSystem(ctx, canvasWidth, canvasHeight) {
        // 保存当前状态
        ctx.save()
        
        // 设置坐标系样式
        ctx.strokeStyle = 'rgba(255, 240, 0, 1)' // 黄色
        ctx.fillStyle = 'rgba(255, 240, 0, 1)'
        ctx.lineWidth = 2
        ctx.font = '14px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        
        // 计算中心点（原点）
        const centerX = canvasWidth / 2
        const centerY = canvasHeight / 2
        
        // 绘制X轴（水平线）
        ctx.beginPath()
        ctx.moveTo(0, centerY)
        ctx.lineTo(canvasWidth, centerY)
        ctx.stroke()
        
        // 绘制Y轴（垂直线）
        ctx.beginPath()
        ctx.moveTo(centerX, 0)
        ctx.lineTo(centerX, canvasHeight)
        ctx.stroke()
        
        // 绘制原点（中心点）
        ctx.beginPath()
        ctx.arc(centerX, centerY, 4, 0, 2 * Math.PI)
        ctx.fill()
        
        // 绘制X轴刻度
        const xStep = canvasWidth / 8 // 将X轴分为8段
        for (let i = 0; i <= 8; i++) {
            const x = i * xStep
            const value = Math.round((i - 4) * 240) // 对应 -960 到 960
            
            // 绘制刻度线
            ctx.beginPath()
            ctx.moveTo(x, centerY - 5)
            ctx.lineTo(x, centerY + 5)
            ctx.stroke()
            
            // 绘制刻度值 - 添加边界检测
            let textX = x
            let textY = centerY + 20
            
            // 检查X轴边界，调整文字位置
            if (i === 0) {
                // 最左边的数字，向右偏移避免超出左边界
                textX = x + 15
            } else if (i === 8) {
                // 最右边的数字，向左偏移避免超出右边界
                textX = x - 15
            } else if (i === 4) {
                textX = x - 15
            }
            
            // 检查Y轴边界，调整文字位置
            if (textY > canvasHeight - 10) {
                // 如果文字会超出下边界，放到上方
                textY = centerY - 20
            }
            
            ctx.fillText(value.toString(), textX, textY)
        }
        
        // 绘制Y轴刻度
        const yStep = canvasWidth / 8 // 使用与X轴相同的尺寸计算
        for (let i = 0; i <= 8; i++) {
            const y = i * yStep
            const value = Math.round((4 - i) * 240) // 对应 -960 到 960，与X轴完全一致
            
            // 绘制刻度线
            ctx.beginPath()
            ctx.moveTo(centerX - 5, y)
            ctx.lineTo(centerX + 5, y)
            ctx.stroke()
            
            // 绘制刻度值 - 添加边界检测
            let textX = centerX - 20
            let textY = y
            
            // 检查Y轴边界，调整文字位置
            if (i === 0) {
                // 最上边的数字，向下偏移避免超出上边界
                textY = y + 15
            } else if (i === 8) {
                // 最下边的数字，向上偏移避免超出下边界
                textY = y - 15
            }
            
            // 检查X轴边界，调整文字位置
            if (textX < 10) {
                // 如果文字会超出左边界，放到右侧
                textX = centerX + 20
            }
            ctx.fillText(value.toString(), textX, textY)
        }
        
        // 绘制坐标轴标签
        ctx.fillText('X', canvasWidth - 20, centerY - 20)
        ctx.fillText('Y', centerX + 20, 20)
        
        // 恢复状态
        ctx.restore()
    }

    // 绘制二维码边框的函数
    function drawQRCodeBorder(ctx, location) {
        // 设置边框样式
        ctx.strokeStyle = 'yellow' // 绿色边框
        ctx.lineWidth = 4
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        
        // 绘制边框
        ctx.beginPath()
        ctx.moveTo(location.topLeftCorner.x, location.topLeftCorner.y)
        ctx.lineTo(location.topRightCorner.x, location.topRightCorner.y)
        ctx.lineTo(location.bottomRightCorner.x, location.bottomRightCorner.y)
        ctx.lineTo(location.bottomLeftCorner.x, location.bottomLeftCorner.y)
        ctx.closePath()
        ctx.stroke()
        
        // 可选：在四个角绘制小方块标记
        const cornerSize = 4
        ctx.fillStyle = 'yellow'
        
        // 左上角
        ctx.fillRect(location.topLeftCorner.x - cornerSize/2, location.topLeftCorner.y - cornerSize/2, cornerSize, cornerSize)
        // 右上角
        ctx.fillRect(location.topRightCorner.x - cornerSize/2, location.topRightCorner.y - cornerSize/2, cornerSize, cornerSize)
        // 右下角
        ctx.fillRect(location.bottomRightCorner.x - cornerSize/2, location.bottomRightCorner.y - cornerSize/2, cornerSize, cornerSize)
        // 左下角
        ctx.fillRect(location.bottomLeftCorner.x - cornerSize/2, location.bottomLeftCorner.y - cornerSize/2, cornerSize, cornerSize)
    }

    // 跳转到英荔二维码
    function linkQrCode() {
        try {
            // 检查是否已经有打开的窗口
            if (openerRef.current && !openerRef.current.closed) {
                // 如果窗口已存在且未关闭，则聚焦到该窗口
                console.log('二维码生成器页面已存在，聚焦到现有窗口')
                openerRef.current.focus()
            } else {
                // 如果没有打开的窗口，则创建新窗口
                openerRef.current = window.open('https://qrcode.aimaker.space')
                    
                // 检测窗口是否打开成功
                if (openerRef.current && !openerRef.current.closed) {
                    // 保存窗口引用到全局变量
                    console.log('二维码生成器页面打开成功')
                        
                    // 监听窗口关闭事件，清理引用
                    openerRef.current.onbeforeunload = () => {
                        openerRef.current = null
                        console.log('二维码生成器页面已关闭')
                    }
                } else {
                    console.log('二维码生成器页面打开失败')
                }
            }
        } catch (error) {
            console.error('打开页面时发生错误:', error)
        }
    }
    // 收缩展开
    const { isOpen, setIsOpen, height, ref } = useCollapse()
    return (
        <div className='qrcode plug-in'>
            <div className='plug-in-name'>二维码（QR Code）</div>
            <div className='plug-in-content'>
                <div className='desc-content'>
                    <div className='desc-content describe'>
                        <div className="qrcode__desc">
                            二维码（two-dimensional code）是一个总称，指所有能在二维平面上用黑白或彩色图形编码信息的条码形式，包括 QR Code、Data Matrix、PDF417、Aztec 等多种类型。QR Code（Quick Response Code）是其中的一种，由日本 Denso Wave 公司在 1994 年发明，特点是信息容量大、读取速度快、支持高纠错率，并且开放专利供免费使用，目前已经成为了最普及的二维码类型之一。
                        </div>
                        <div className="qrcode__bold">
                            一个标准的 QR Code 一般分为功能图案和编码区：
                        </div>
                        <div className="qrcode__desc">
                            功能图案包括定位、对齐、时序、格式、版本等区域。其中定位图案最为直观，一般在左上、右上、左下三个角，总共 3 个大方块，用于帮助扫描器快速判断二维码位置与角度。而编码区就是承载实际信息和容错码的地方。容错码可以帮助 QR Code 在部分破损后仍旧可以读取出完整的信息。
                        </div>
                        <div className='qrcode__bold'>
                            扫描系统读取 QR Code 的过程：
                        </div>
                        <div className="qrcode__list qrcode__desc">
                            <p><div className="qrcode__weight">1. 图像采集：</div>用摄像头拍下二维码图像。</p>
                            <p><div className="qrcode__weight">2. 预处理：</div>将彩色图转换为灰度，再进行二值化处理（变为完全非黑即白的图像）。</p>
                            <p><div className="qrcode__weight">3. 定位与倾斜矫正：</div>识别出定位图案，确定二维码的边界和角度，通过透视变换等技术还原成正方形。</p>
                            <p><div className="qrcode__weight">4. 网格解析数据解码：</div>确定一个个小方块的大小和位置后，按特定的编码顺序将黑白小方块转为二进制数据（黑=1，白=0）。</p>
                            <p><div className="qrcode__weight">5. 纠错还原：</div>用 Reed–Solomon 算法修正因污损、遮挡或拍摄不清造成的错误。</p>
                            <p><div className="qrcode__weight">6. 内容输出：</div>将二进制数据按编码格式（如 UTF-8、Shift_JIS 等）还原成文本、网址、指令等可用的信息。</p>
                        </div>
                        <div className="qrcode__desc">
                            <span onClick={() => setIsOpen(!isOpen)}>影响识别结果准确率的因素有哪些？<span className="qrcode__link">{isOpen ? '点击折叠起来' : '点击看答案'}</span></span>
                            <ol className="qrcode__list qrcode__collapse" style={{maxHeight: height}} ref={ref}>
                                <li className='qrcode__bold'>
                                图像质量与环境因素<br/>
                                </li>
                                <span><span className='qrcode__label'>清晰度：</span>拍摄模糊（镜头模糊、手持拍摄抖动等）、摄像头的分辨率不足（设备本身影像性能较弱、出从较远距离拍摄、过于倾斜地拍摄等），会给预处理带来难度。一般来说，现代智能手机的影像性能远高于电脑摄像头。</span><br/>
                                <span><span className='qrcode__label'>光照条件：</span>光线过暗或过亮会给照片增加噪点或产生反光，进而影响二值化处理。</span><br/>
                                <span><span className='qrcode__label'>对比度：</span>黑白对比不够（特别是一些彩色的二维码），扫描器更难区分 1 和 0。</span><br/>
                                <span><span className='qrcode__label'>污损/遮挡：</span>过大的脏污、折痕、局部被盖住，会造成信息丢失。</span><br/>

                                <li className='qrcode__bold'>
                                物理打印与展示质量<br/>
                                </li>
                                <span><span className='qrcode__label'>模块尺寸过小：</span>打印得太细小会让摄像头难以分辨出小方块在什么位置。一些经过美化的用圆形代替小正方形的二维码，其实是会提升识别难度的。</span><br/>
                                <span><span className='qrcode__label'>比例失衡：</span>被拉伸、压缩或非等比缩放，会破坏网格结构。这常见于海报印刷或电视大屏播放的场景。</span><br/>
                                <span><span className='qrcode__label'>材质反光：</span>光面纸、玻璃等会造成反射影响拍摄。这常见于海报印刷或电视大屏播放的场景。</span>

                                <li className='qrcode__bold'>
                                编码设计与容错能力<br/>
                                </li>
                                <span><span className='qrcode__label'>版本选择：</span>QR Code 也在不断更新，新版本的 QR Code 可以容纳更多信息。因为如果信息量很大，在同样尺寸的二维码中留给用于容错的空间就越少，导致拍摄质量稍有下降或是二维码有细微损坏也无法读取信息。</span><br/>
                                <span><span className='qrcode__label'>纠错等级：</span>为了适应不同使用场景下对二维码小尺寸的追求，QR Code 可以选择 L、M、Q、H 等不同等级的容错能力。越强的容错能力，就需要占用越多的空间。</span>
                            </ol>
                        </div>
                        <div className="qrcode__block">
                            <div className='qrcode__platform'>
                                本扩展可在互联创作平台使用，或连接主控硬件后结合硬件编程平台使用。
                            </div>
                            <div className='qrcode__platform'>
                                没有二维码？想制作属于自己的二维码？请点击：
                                <span onClick={linkQrCode} className="qrcode__qrlink">英荔二维码生成器</span>
                            </div>
                            <div className="qrcode__flex">
                                <div className="qrcode__video">
                                    <div className="qrcode__block">
                                        <span className="qrcode__title">摄像头画面</span>
                                        {isCameraAvailable ? <div className='desc'>
                                            <div className="video-container" style={{ position: 'relative', display: 'inline-block' }}>
                                                <video ref={videoRef} muted loop playsInline autoPlay></video>
                                                <canvas 
                                                    ref={videoCoordinateRef} 
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: 10
                                                    }}
                                                ></canvas>
                                            </div>
                                        </div> : <>
                                            <div className='desc'>
                                                <img src={notCamera} alt="" />
                                                <div className='qrcode__desc-text'>没有找到摄像头</div>
                                            </div>
                                            <div className='tip is-abs'>
                                                <div className='icon'><InfoCircleFilled /></div>
                                                请允许浏览器调用摄像头（camera）
                                            </div>
                                        </>}
                                    </div>
                                </div>
                                <div className="qrcode__video">
                                    <span className="qrcode__title">最近一次扫描的画面与结果</span>
                                    {
                                        hasImage ? 
                                        <div className='qrcode__scanned qrcode__block'>
                                            <div className='desc'>
                                                <canvas ref={canvasRef}></canvas>
                                            </div>
                                             <div className='tip'>
                                                开启边界示意图？ <Switch size='small' checked={switchChecked} onChange={handleChangeBorder} />
                                            </div>
                                        </div> 
                                        :
                                        <div className='qrcode__block'>
                                            <div className='desc'>
                                                <img src={notScan} alt="" />
                                                <div className='qrcode__desc-text'>暂未进行过扫描</div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className='qrcode__result'>
                                <div className='qrcode__use-data'></div>
                                <div className='qrcode__scan-data'>
                                    <div className='qrcode__scan-title'>显示结果</div>
                                    { !loading ? <div className='qrcode__scan-content'>
                                        <div style={{wordBreak: 'break-all'}}>
                                            <span>数据：</span>{coordinates ? coordinates.text : '-'}
                                        </div>
                                        <div style={{marginTop: '12px', marginBottom: '12px'}}>
                                            <span>角度<Tooltip title="头向左是 0°，正正放置时为 90°，头向右是 180°，值为 -180°~180°">
                                                <ExclamationCircleOutlined style={{ marginLeft: '5px', marginRight: '5px', color: '#999' }} />
                                            </Tooltip>：</span>{coordinates ? coordinates.angle + '°' : '-'}
                                        </div>
                                        <div className='qrcode__position'>
                                            <span>位置：</span> {!coordinates && '-'}
                                            {coordinates && <div>
                                                <p>
                                                    中心点的坐标（{coordinates.coord.center.x}
                                                    ,{coordinates.coord.center.y}）
                                                </p>
                                                <p>
                                                    左上角的坐标（{coordinates.coord.topLeft.x}
                                                    ,{coordinates.coord.topLeft.y}）
                                                </p>
                                                <p>
                                                    右上角的坐标（{coordinates.coord.topRight.x}
                                                    ,{coordinates.coord.topRight.y}）
                                                </p>
                                                <p>
                                                    左下角的坐标（{coordinates.coord.bottomLeft.x}
                                                    ,{coordinates.coord.bottomLeft.y}）
                                                </p>
                                                <p>
                                                    右下角的坐标（{coordinates.coord.bottomRight.x}
                                                    ,{coordinates.coord.bottomRight.y}）
                                                </p>
                                            </div>}
                                        </div>
                                    </div> : '...'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default QRCode;